import { type FunctionComponent, startTransition, useMemo, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import type { topicCollectionDescriptionTopic$key } from "scmp-app/queries/__generated__/topicCollectionDescriptionTopic.graphql";

import {
  Container,
  Description,
  DescriptionContainer,
  ShowButton,
  Title,
  TitleContainer,
  TitleRow,
} from "./styles";

type Props = {
  className?: string;
  reference: topicCollectionDescriptionTopic$key;
};

export const TopicCollectionDescription: FunctionComponent<Props> = ({ className, reference }) => {
  const topic = useFragment(
    graphql`
      fragment topicCollectionDescriptionTopic on Collection {
        name
        description {
          text
        }
      }
    `,
    reference,
  );

  const [isShowFullProfile, setIsShowFullProfile] = useState(false);
  const isHavingMoreContent = useMemo(() => {
    const textLength = topic?.description?.text?.length ?? 0;
    return textLength > 100;
  }, [topic?.description?.text?.length]);

  const containerReference = useRef<HTMLDivElement>(null);

  const description = useMemo(
    () =>
      findFirstNonEmptyString(
        topic.description?.text,
        `The latest news, analysis and opinion on ${topic.name}. In-depth analysis, industry insights and expert opinion.`,
      ),
    [topic.description?.text, topic.name],
  );

  return (
    <Container className={className} ref={containerReference}>
      <TitleContainer>
        <TitleRow>
          <Title>{topic.name}</Title>
        </TitleRow>
      </TitleContainer>
      <DescriptionContainer
        $isHavingMoreContent={isHavingMoreContent}
        $isShowFullProfile={isShowFullProfile}
      >
        <Description>{description}</Description>
        <ShowButton
          onClick={() => {
            setIsShowFullProfile(true);
            startTransition(() => {
              setTimeout(() => {
                containerReference.current?.scrollIntoView({ behavior: "smooth" });
              }, 150);
            });
          }}
        >
          SHOW FULL PROFILE
        </ShowButton>
      </DescriptionContainer>
    </Container>
  );
};

TopicCollectionDescription.displayName = "TopicCollectionDescription";
